import React from 'react'

import { BorderStyle, Color, Text, withCitrusTheme } from '@gousto-internal/citrus-react'
import type { FlexPortion } from '@library/api-menu-experience'

const PortionButton = withCitrusTheme('button', (theme) => ({
  alignItems: 'center',
  appearance: 'none',
  aspectRatio: '1 / 1',
  backgroundColor: theme.color(Color.ColdGrey_50),
  borderColor: theme.color(Color.ColdGrey_100),
  borderRadius: '999px',
  borderStyle: BorderStyle.Solid,
  borderWidth: theme.border.width(0.5).borderWidth,
  color: theme.color(Color.ColdGrey_800),
  cursor: 'pointer',
  display: 'inline-flex',
  font: 'inherit',
  justifyContent: 'center',
  maxWidth: '60px',
  minWidth: 0,
  padding: 0,
  position: 'relative',
  width: '100%',

  '&:not(:disabled):hover, &[aria-pressed="true"]': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderColor: theme.color(Color.Secondary_400),
  },

  '&:focus-visible': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderColor: theme.color(Color.Secondary_400),
    outline: `2px solid ${theme.color(Color.Secondary_600)}`,
    outlineOffset: '2px',
  },

  '&:disabled': {
    backgroundColor: theme.color(Color.ColdGrey_50),
    borderColor: theme.color(Color.ColdGrey_100),
    color: theme.color(Color.ColdGrey_300),
    cursor: 'not-allowed',
  },

  '&:disabled::after': {
    backgroundColor: theme.color(Color.ColdGrey_200),
    content: '""',
    height: theme.border.width(0.5).borderWidth,
    left: 0,
    position: 'absolute',
    top: '50%',
    transform: 'rotate(-45deg)',
    width: '100%',
  },

  '& span': {
    position: 'relative',
    zIndex: 1,
  },
}))

const PortionPickerGrid = withCitrusTheme('div', (theme) => ({
  display: 'grid',
  gap: `clamp(2px, 0.8vw, ${theme.spacing(1)}px)`,
  gridTemplateColumns: 'repeat(5, minmax(36px, 60px))',
  justifyContent: 'space-between',
  margin: '0 auto',
  maxWidth: '480px',
  width: '100%',
}))

type FlexPortionPickerProps = {
  portions: FlexPortion[]
  selectedNumPortions: number
  onChange: (numPortions: number) => void
}

export const FlexPortionPicker = ({
  portions,
  selectedNumPortions,
  onChange,
}: FlexPortionPickerProps) => (
  <PortionPickerGrid>
    {portions.map(({ label, numPortions, isAvailable }) => {
      const isSelected = numPortions === selectedNumPortions
      const isUnavailable = isAvailable === false
      const accessibleLabel = `${label} ${numPortions === 1 ? 'portion' : 'portions'}${
        isUnavailable ? ' unavailable' : ''
      }`

      return (
        <PortionButton
          aria-label={accessibleLabel}
          aria-pressed={isSelected}
          disabled={isUnavailable && !isSelected}
          key={numPortions}
          onClick={() => onChange(numPortions)}
          type="button"
        >
          <Text size={2}>{label}</Text>
        </PortionButton>
      )
    })}
  </PortionPickerGrid>
)
